import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, ClipboardCheck, Calculator, FileText, ShieldCheck, AlertTriangle, CheckCircle,
  Briefcase, MapPin, HelpCircle, Globe, Package, BarChart3, Users, Layers, Plane, Code2,
  Factory, Camera, Scale, ArrowRight, Lightbulb
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import RelatedServices from '../RelatedServices';
import { freeZoneList } from '../../data/freeZones';

export const ICONS = {
  audit: ClipboardCheck, ledger: Calculator, tax: FileText, vat: ShieldCheck, aml: AlertTriangle,
  advisory: Building2, globe: Globe, box: Package, chart: BarChart3, users: Users, layers: Layers,
  plane: Plane, code: Code2, factory: Factory, camera: Camera, scale: Scale
};

const DEFAULT_SERVICES = [
  { icon: 'audit', title: 'Audit Services', description: 'Annual audited financial statements prepared in the format expected at licence renewal — IFRS-compliant and FTA-ready.' },
  { icon: 'ledger', title: 'Accounting & Bookkeeping', description: 'Cloud bookkeeping on Zoho Books or QuickBooks, monthly reporting and reconciliations tailored to your activity mix.' },
  { icon: 'tax', title: 'Corporate Tax Services', description: 'CT registration, QFZP / Small Business Relief analysis, annual return filing and TP review where required.' },
  { icon: 'vat', title: 'VAT Compliance', description: 'VAT registration, quarterly filing, reconciliation and FTA correspondence — including export and Designated Zone treatment.' },
  { icon: 'aml', title: 'AML / goAML Support', description: 'DNFBP assessments, goAML registration and ongoing AML compliance documentation where activities are regulated.' },
  { icon: 'advisory', title: 'Renewal & Advisory', description: 'Practical advisory across financial reporting, internal controls and audit support during your licence renewal cycle.' }
];

const DEFAULT_RELATED = [
  { title: 'UAE Free Zone Services', description: 'Master page covering audit, tax and compliance across all UAE free zones.', path: '/free-zone-services-uae', accent: 'teal' },
  { title: 'Hamriyah Free Zone Services', description: 'Dedicated audit, VAT and Corporate Tax support for Hamriyah entities.', path: '/hamriyah-free-zone-services', accent: 'emerald' },
  { title: 'SAIF Zone Services', description: 'Audit, accounting and compliance for SAIF Zone businesses.', path: '/saif-zone-services', accent: 'blue' }
];

const EXTRA_ZONES = [
  { name: 'Hamriyah Free Zone', slug: '/hamriyah-free-zone-services' },
  { name: 'SAIF Zone', slug: '/saif-zone-services' }
];

const SectionHead = ({ a, eyebrow, title, intro, left = false }) => (
  <div className={left ? 'mb-10 max-w-3xl' : 'text-center mb-12'}>
    <span className={`${a.txt} font-semibold text-sm uppercase tracking-wider`}>{eyebrow}</span>
    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">{title}</h2>
    {intro && <p className={`text-lg text-gray-600 ${left ? '' : 'max-w-3xl mx-auto'}`}>{intro}</p>}
  </div>
);

const Shell = ({ bg, narrow, children, testId }) => (
  <section className={`py-20 px-4 ${bg}`} data-testid={testId}>
    <div className={`container mx-auto ${narrow ? 'max-w-5xl' : 'max-w-6xl'}`}>{children}</div>
  </section>
);

export const AuthorityBlock = ({ zone, a, bg }) => (
  <Shell bg={`${bg} border-b border-gray-100`} narrow testId="fz-block-authority">
    <div className="grid md:grid-cols-3 gap-8">
      <div className="md:col-span-2">
        <span className={`${a.txt} font-semibold text-sm uppercase tracking-wider`}>About the Authority</span>
        <h2 className="text-3xl font-bold text-gray-900 mt-3 mb-4">{zone.fullName}</h2>
        {zone.authorityIntro.map((p, i) => <p key={i} className="text-gray-600 leading-relaxed mb-3">{p}</p>)}
        <p className="text-sm text-gray-500 flex items-center gap-1 mt-2"><MapPin size={14} /> {zone.emirate}, UAE</p>
      </div>
      <div className={`${a.tile} rounded-2xl p-6 border`}>
        <h3 className="font-bold text-gray-900 mb-3">Typical {zone.name} Compliance Cycle</h3>
        <ul className="space-y-2 text-sm text-gray-700">
          {zone.cyclePoints.map((c, i) => (
            <li key={i} className="flex gap-2"><CheckCircle size={16} className={`${a.txt} mt-0.5 flex-shrink-0`} /> {c}</li>
          ))}
        </ul>
      </div>
    </div>
  </Shell>
);

export const FactsBlock = ({ zone, a, bg }) => (
  <Shell bg={bg} narrow testId="fz-block-facts">
    <SectionHead a={a} eyebrow="At a Glance" title={`${zone.name} Key Facts for Finance Teams`} left />
    <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
      {zone.facts.map((f) => (
        <div key={f.label} className="bg-white p-6">
          <dt className={`text-xs font-semibold uppercase tracking-wider ${a.txt} mb-2`}>{f.label}</dt>
          <dd className="text-gray-800 text-sm leading-relaxed">{f.value}</dd>
        </div>
      ))}
    </dl>
  </Shell>
);

export const ServicesBlock = ({ zone, a, bg }) => (
  <Shell bg={bg} testId="fz-block-services">
    <SectionHead a={a} eyebrow="Services" title={zone.servicesTitle || `What WPAA Delivers in ${zone.name}`} intro={zone.servicesIntro} />
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {(zone.services || DEFAULT_SERVICES).map((svc) => {
        const Icon = ICONS[svc.icon] || Building2;
        return (
          <Card key={svc.title} className={`border-2 ${a.borderHover} transition-shadow hover:shadow-lg`}>
            <CardContent className="p-6">
              <div className={`${a.bg} w-14 h-14 rounded-xl flex items-center justify-center mb-4`}><Icon className={a.txt} size={28} /></div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{svc.title}</h3>
              <p className="text-gray-600">{svc.description}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  </Shell>
);

export const ChallengesBlock = ({ zone, a, bg }) => (
  <Shell bg={bg} testId="fz-block-challenges">
    <SectionHead a={a} eyebrow="Common Pain Points" title={`Where ${zone.name} Companies Need Help`} intro={zone.challengesIntro || `The recurring compliance and reporting issues we see with ${zone.name} clients.`} />
    <div className="grid md:grid-cols-2 gap-5">
      {zone.challenges.map((c) => (
        <Card key={c.title} className="border border-gray-100 hover:shadow-md transition-shadow">
          <CardContent className="p-6 flex gap-4">
            <AlertTriangle className="text-amber-500 flex-shrink-0 mt-1" size={22} />
            <div>
              <h3 className="font-bold text-gray-900 mb-1">{c.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{c.description}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  </Shell>
);

export const SectorsBlock = ({ zone, a, bg }) => (
  <Shell bg={bg} narrow testId="fz-block-sectors">
    <SectionHead a={a} eyebrow="Sectors" title={`Sectors We Serve in ${zone.name}`} />
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {zone.sectors.map((s) => (
        <div key={s} className={`bg-white rounded-xl p-5 text-center border border-gray-100 ${a.borderHover} transition-colors`}>
          <Briefcase className={`${a.txt} mx-auto mb-2`} size={28} />
          <p className="font-semibold text-gray-800 text-sm">{s}</p>
        </div>
      ))}
    </div>
  </Shell>
);

const DeepDiveTable = ({ table }) => (
  <div className="overflow-x-auto rounded-2xl border border-gray-200 mt-10" data-testid="fz-deepdive-table">
    <table className="w-full min-w-[640px] text-sm text-left">
      <thead className="bg-gray-900 text-white">
        <tr>{table.headers.map((h) => <th key={h} className="px-5 py-4 font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-gray-100 bg-white">
        {table.rows.map((row) => (
          <tr key={row[0]}>
            {row.map((cell, i) => <td key={i} className={`px-5 py-4 align-top ${i === 0 ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const DeepDiveBlock = ({ zone, a, bg }) => {
  const d = zone.deepDive;
  return (
    <Shell bg={bg} testId="fz-block-deepdive">
      <SectionHead a={a} eyebrow={d.eyebrow} title={d.title} intro={d.intro} left />
      <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
        {d.points.map((p, i) => (
          <div key={p.title} className="flex gap-4">
            <span className={`${a.bg} ${a.txt} w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0`}>{i + 1}</span>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">{p.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{p.body}</p>
            </div>
          </div>
        ))}
      </div>
      {d.table && <DeepDiveTable table={d.table} />}
      {d.note && <p className="text-xs text-gray-500 mt-8 max-w-3xl">{d.note}</p>}
    </Shell>
  );
};

export const ScenarioBlock = ({ zone, a, bg }) => {
  const s = zone.scenario;
  return (
    <Shell bg={bg} narrow testId="fz-block-scenario">
      <SectionHead a={a} eyebrow="Worked Example" title={s.title} left />
      <div className="grid lg:grid-cols-5 gap-6">
        <div className={`lg:col-span-2 ${a.tile} border rounded-2xl p-6`}>
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2"><Lightbulb size={18} className={a.txt} /> The situation</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{s.situation}</p>
        </div>
        <div className="lg:col-span-3 bg-white border border-gray-200 rounded-2xl p-6">
          <h3 className="font-bold text-gray-900 mb-3">How we would approach it</h3>
          <ol className="space-y-3">
            {s.approach.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm text-gray-700 leading-relaxed">
                <span className={`${a.txt} font-bold`}>{String(i + 1).padStart(2, '0')}</span>{step}
              </li>
            ))}
          </ol>
          <p className="mt-5 pt-5 border-t border-gray-100 text-sm text-gray-800"><strong>Outcome: </strong>{s.outcome}</p>
        </div>
      </div>
    </Shell>
  );
};

export const ChecklistBlock = ({ zone, a, bg }) => {
  const c = zone.checklist;
  return (
    <Shell bg={bg} narrow testId="fz-block-checklist">
      <SectionHead a={a} eyebrow="Checklist" title={c.title} intro={c.intro} left />
      <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
        {c.items.map((item) => (
          <li key={item} className="flex gap-3 text-gray-700 text-sm leading-relaxed border-b border-gray-100 pb-4">
            <CheckCircle size={18} className={`${a.txt} flex-shrink-0 mt-0.5`} />{item}
          </li>
        ))}
      </ul>
    </Shell>
  );
};

export const TimelineBlock = ({ zone, a, bg }) => {
  const t = zone.timeline;
  return (
    <Shell bg={bg} narrow testId="fz-block-timeline">
      <SectionHead a={a} eyebrow="Compliance Calendar" title={t.title} intro={t.intro} left />
      <ol className="relative border-l-2 border-gray-200 ml-3 space-y-8">
        {t.steps.map((s) => (
          <li key={s.when} className="pl-8 relative">
            <span className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full ${a.btnPrimary.split(' ')[0]}`} />
            <p className={`text-xs font-semibold uppercase tracking-wider ${a.txt}`}>{s.when}</p>
            <p className="text-gray-800 mt-1 leading-relaxed">{s.what}</p>
          </li>
        ))}
      </ol>
    </Shell>
  );
};

export const FaqAccordionBlock = ({ zone, a, bg }) => (
  <Shell bg={bg} narrow testId="fz-block-faqs">
    <SectionHead a={a} eyebrow="FAQs" title={`${zone.name} — FAQs`} />
    <Accordion type="single" collapsible className="space-y-4 max-w-3xl mx-auto">
      {zone.faqs.map((faq, index) => (
        <AccordionItem key={faq.q} value={`item-${index}`} className="bg-gray-50 border border-gray-200 rounded-xl px-6 shadow-sm">
          <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:no-underline">
            <div className="flex items-start gap-3"><HelpCircle className={`${a.txt} flex-shrink-0 mt-1`} size={20} /><span>{faq.q}</span></div>
          </AccordionTrigger>
          <AccordionContent className="text-gray-600 text-base leading-relaxed pl-8">{faq.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </Shell>
);

export const FaqListBlock = ({ zone, a, bg }) => (
  <Shell bg={bg} testId="fz-block-faqs">
    <SectionHead a={a} eyebrow="FAQs" title={`${zone.name} Audit, VAT & Corporate Tax Questions`} />
    <div className="grid md:grid-cols-2 gap-6">
      {zone.faqs.map((faq, i) => (
        <div key={faq.q} className="bg-white border border-gray-200 rounded-2xl p-6" data-testid={`fz-faq-${i}`}>
          <h3 className="font-bold text-gray-900 mb-2 flex gap-2"><HelpCircle className={`${a.txt} flex-shrink-0 mt-0.5`} size={18} />{faq.q}</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
        </div>
      ))}
    </div>
  </Shell>
);

export const RelatedBlock = ({ zone }) => <RelatedServices services={zone.related || DEFAULT_RELATED} />;

export const SiblingsBlock = ({ zone, a }) => (
  <section className="py-12 px-4 bg-gray-50 border-t border-gray-100" data-testid="fz-block-siblings">
    <div className="container mx-auto max-w-6xl">
      <h2 className="text-lg font-bold text-gray-900 mb-4">Other UAE free zones we support</h2>
      <div className="flex flex-wrap gap-3">
        {[...freeZoneList.filter((z) => z.slug !== zone.slug), ...EXTRA_ZONES].map((z) => (
          <Link key={z.slug} to={z.slug} className={`group inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full px-4 py-2 text-sm font-medium text-gray-700 ${a.borderHover} transition-colors`} data-testid={`fz-sibling-${z.slug.slice(1)}`}>
            {z.name} services <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export const BLOCKS = {
  authority: AuthorityBlock, facts: FactsBlock, services: ServicesBlock, challenges: ChallengesBlock,
  sectors: SectorsBlock, deepDive: DeepDiveBlock, scenario: ScenarioBlock, checklist: ChecklistBlock,
  timeline: TimelineBlock, faqs: FaqAccordionBlock, faqList: FaqListBlock, related: RelatedBlock, siblings: SiblingsBlock
};

export const DEFAULT_LAYOUT = ['authority', 'services', 'challenges', 'sectors', 'faqs', 'related'];
